/**
 * The ocean: everywhere the terrain is at or below sea level.
 *
 * ## Why a single surface rather than water tiles
 *
 * Sea level is, by definition, the altitude datum — so the ocean is one surface at `up = 0` in
 * the sim frame, and the terrain either pokes through it or does not. Nothing has to decide
 * which tiles are wet, no coastline has to be extracted, and the shoreline falls out of the
 * depth test for free and at full resolution: the water is drawn where the ground is below it,
 * which is exactly what a coastline is.
 *
 * ## Why the mesh is a polar disc with geometric ring spacing
 *
 * This is the change that makes M3's Gerstner swell possible at all, so it is worth being
 * explicit. M2's ocean was a 192×192 grid spanning 440 km — 2.3 km between vertices. Ocean
 * swell has a wavelength of tens to a couple of hundred metres. Displacing that grid vertically
 * would have sampled the wave field at a thousandth of its frequency and produced, exactly,
 * noise. Any amount of "add Gerstner waves" on that mesh is wasted work.
 *
 * So the mesh is now a disc in polar coordinates whose rings grow *geometrically*: each ring is
 * about 4 % further out than the last, from four metres to the draw distance. Vertex spacing is
 * therefore proportional to range — roughly 4 m at 100 m out, 40 m at a kilometre, 400 m at ten
 * — which is the same "constant angular size" principle the terrain quadtree uses, and it means
 * the tessellation is finest exactly where the waves are resolvable and coarsest where they
 * are not. Same vertex count as before, three orders of magnitude more useful.
 *
 * The consequence is that each wave has to fade out where the mesh can no longer represent it,
 * or it aliases into a crawling moiré as the disc slides under the camera. Every wave is
 * therefore faded against the local vertex spacing, and what it fades *into* is the normal-map
 * detail, which costs nothing per unit area and is all the eye can resolve past a kilometre
 * anyway.
 *
 * ## What is modelled
 *
 *   - **Gerstner swell.** Trochoidal waves — crests sharp, troughs broad — which is what real
 *     deep-water waves do and what a plain sine does not. Directions spread about the wind,
 *     wavelengths and speeds from the deep-water dispersion relation `ω = √(gk)`, so long waves
 *     genuinely outrun short ones and the pattern never repeats visibly.
 *   - **Wind-driven sea state.** Wave height follows the fully-developed relation `H ≈ 0.021·U²`,
 *     so a 5 m/s breeze gives half a metre of swell and a 15 m/s gale gives five. One number,
 *     from the weather, drives the whole surface.
 *   - **Whitecaps.** Real whitecaps appear where a wave is steep enough to break, so they are
 *     driven by the wave *steepness* the Gerstner sum already computes, gated on wind speed —
 *     they start around 6 m/s, which is Beaufort 4, which is where they start on the sea.
 *   - **Sun glitter.** The specular lobe is widened by wind using the Cox–Munk slope variance
 *     `σ² ≈ 0.003 + 0.00512·U`, measured from sun-glitter photographs for precisely this
 *     purpose. Calm water gives a single hard highlight; a windy sea gives the long glitter path.
 *   - **Fresnel.** Water is nearly a mirror at grazing angles and nearly transparent looking
 *     straight down. Getting this one relationship right is most of what makes water read as
 *     water.
 *
 * `updateFrame(alpha)`: yes — advances the wave clock and re-centres the disc on the camera.
 * Allocation-free.
 */

import { BufferAttribute, BufferGeometry, Color, DoubleSide, Mesh, Vector2, Vector3 } from 'three';
import { MeshStandardNodeMaterial } from 'three/webgpu';
import type { Node } from 'three/webgpu';
import {
  cameraPosition,
  clamp,
  cos,
  dot,
  float,
  max,
  mix,
  normalize,
  positionGeometry,
  positionWorld,
  pow,
  sin,
  smoothstep,
  transformNormalToView,
  uniform,
  vec2,
  vec3,
} from 'three/tsl';
import type { QualitySettings } from '../core/quality.js';
import type { CloudShadow } from './clouds/cloudShadow.js';

/** Standard gravity [m/s²] — the deep-water dispersion relation's only constant. */
const GRAVITY = 9.80665;

/**
 * The wave set, compiled once.
 *
 * Eight waves are always in the shader and the tier zeroes the amplitude of the ones it does not
 * want, rather than the wave count changing the generated code. A tier step must not trigger a
 * shader recompile — on WebGPU that is milliseconds of main-thread pipeline building, which
 * would show up as a stutter at exactly the moment the governor stepped down to *avoid* a
 * stutter.
 *
 * Wavelengths span 20 m to 320 m. The spread of directions about the wind is deliberate and
 * wide: a real sea is not a set of parallel ridges, and the interference between trains at
 * different angles is what produces the irregular, non-repeating surface the eye expects.
 */
const WAVES: readonly { wavelengthM: number; amplitudeScale: number; angleOffsetDeg: number; steepness: number }[] = [
  { wavelengthM: 320, amplitudeScale: 1.0, angleOffsetDeg: 0, steepness: 0.42 },
  { wavelengthM: 187, amplitudeScale: 0.72, angleOffsetDeg: 27, steepness: 0.4 },
  { wavelengthM: 113, amplitudeScale: 0.52, angleOffsetDeg: -34, steepness: 0.38 },
  { wavelengthM: 71, amplitudeScale: 0.36, angleOffsetDeg: 58, steepness: 0.36 },
  { wavelengthM: 44, amplitudeScale: 0.25, angleOffsetDeg: -63, steepness: 0.34 },
  { wavelengthM: 29, amplitudeScale: 0.17, angleOffsetDeg: 16, steepness: 0.32 },
  { wavelengthM: 20, amplitudeScale: 0.12, angleOffsetDeg: -81, steepness: 0.3 },
  { wavelengthM: 13, amplitudeScale: 0.08, angleOffsetDeg: 44, steepness: 0.28 },
];

export interface WaterOptions {
  settings: QualitySettings;
  /** Radius of the water disc [m]. Should reach the tier's draw distance. */
  radiusM?: number;
  /** Rings from the innermost to the rim. */
  rings?: number;
  /** Angular segments. */
  sectors?: number;
  shallowColor?: Color;
  deepColor?: Color;
  /**
   * The cloud layer's ground shadow, applied to the glitter.
   *
   * The ocean is the one surface where a cloud shadow that stopped at the shoreline would be
   * unmissable — at San Francisco half the frame is water. It is applied to the *sun glitter*
   * rather than through `receivedShadowNode`, because the disc has `receiveShadow = false` on
   * purpose: a shadow-map lookup across a 220 km fill-rate-bound surface is not a trade this
   * renderer can make, and the glitter is what the sun on water actually looks like.
   */
  cloudShadow?: CloudShadow;
}

export class Water {
  readonly mesh: Mesh;

  private readonly material: MeshStandardNodeMaterial;
  private readonly time = uniform(0);
  private readonly sunDirection = uniform(new Vector3(0.42, 0.66, 0.28).normalize());
  private readonly sunRadiance = uniform(new Vector3(1, 1, 1));
  /**
   * Radiance of the sky near the horizon — what a grazing reflection actually shows.
   *
   * Pushed from the atmosphere model rather than picked. The first version of this used a
   * hand-chosen pale blue, and because the model's radiances are physical (~0.05, not ~0.7) the
   * sea came out roughly fifteen times brighter than the sky it was supposedly reflecting — a
   * blinding white plain. Any constant here is wrong at some time of day; this one is never
   * wrong, because it is the same number the sky dome is drawing.
   */
  private readonly skyRadiance = uniform(new Vector3(0.05, 0.07, 0.11));
  private readonly shallow;
  private readonly deep;
  /** Where the disc is, so the wave phase can be computed in world space. */
  private readonly patchOrigin = uniform(new Vector2(0, 0));
  /** Significant wave height [m], from the wind. */
  private readonly waveHeight = uniform(0.6);
  /** Unit vector along the wind, in the render frame's XZ plane. */
  private readonly windDirection = uniform(new Vector2(1, 0));
  /** Cox–Munk mean-square slope, which sets how wide the glitter path is. */
  private readonly slopeVariance = uniform(0.02);
  /** 0 = no whitecaps, 1 = a covered sea. */
  private readonly whitecapAmount = uniform(0);
  /** Per-wave enable, so a tier change costs no recompile. */
  private readonly waveEnable = WAVES.map(() => uniform(1));
  /** Global displacement scale — zeroed on the `flat` tier. */
  private readonly displacement = uniform(1);
  /** Angular size of one pixel [rad]; drives the detail-normal anti-aliasing. */
  private readonly metresPerPixel = uniform(0.001);
  /**
   * Ring-to-ring radius ratio, so the shader can recover local vertex spacing from radius.
   *
   * A uniform rather than a shared constant because the ratio depends on the disc's actual
   * radius and ring count, and the tier changes the radius. Duplicating it as a literal is how
   * the anti-alias fade quietly ends up happening at the wrong distance on one tier only.
   */
  private readonly ringGrowth = uniform(1.05);

  private readonly radiusM: number;

  constructor(options: WaterOptions) {
    this.radiusM = options.radiusM ?? 220_000;
    this.shallow = uniform(options.shallowColor ?? new Color(0x2e6b7a));
    this.deep = uniform(options.deepColor ?? new Color(0x081d2e));

    const rings = options.rings ?? 220;
    const geometry = buildPolarDisc(this.radiusM, rings, options.sectors ?? 256);
    this.ringGrowth.value = ringGrowthFor(this.radiusM, rings);

    const material = new MeshStandardNodeMaterial();
    material.metalness = 0.02;
    material.roughness = 0.06;
    // Seen from below when the aircraft is in a valley below sea level, and from above
    // everywhere else.
    material.side = DoubleSide;

    // --- the wave field -------------------------------------------------------------------
    // World-space XZ. The disc follows the camera, so its own coordinates are camera-relative;
    // adding the patch origin back is what keeps the waves anchored to the planet instead of
    // sliding along with the aeroplane.
    const local = vec2(positionGeometry.x, positionGeometry.z);
    const worldXZ = local.add(vec2(this.patchOrigin));

    /*
     * Local vertex spacing, and the anti-aliasing fade it drives.
     *
     * The polar disc's rings are geometric, so spacing is proportional to radius — one multiply
     * recovers it, no attribute needed. A wave shorter than about two vertex spacings cannot be
     * represented and must be faded out; left in, it turns into a crawling interference pattern
     * that is far more distracting than the missing detail.
     */
    const radius = local.length().max(4);
    const spacing = radius.mul(float(this.ringGrowth).sub(1));

    const wave = this.buildGerstner(worldXZ, spacing);

    // Curvature. Done here rather than baked into the geometry because the disc now follows the
    // camera, so a vertex's drop depends on where it currently *is* on the planet, not on where
    // it sits in the mesh.
    const curvature = worldXZ
      .dot(worldXZ)
      .min(this.radiusM * this.radiusM)
      .div(2 * 6_371_008.8);

    material.positionNode = vec3(
      positionGeometry.x.add(wave.offsetX),
      wave.height.sub(curvature),
      positionGeometry.z.add(wave.offsetZ),
    );

    // --- normals --------------------------------------------------------------------------
    // The Gerstner sum gives an analytic slope; detail normals add the ripple the mesh cannot
    // carry. `transformNormalToView` matters — `normalNode` is consumed in *view* space, and a
    // world-space vector put there is a normal that rotates with the camera, which shows up as
    // a sun glint that slides when you look around rather than staying on the water.
    /*
     * How much ocean one pixel covers, in metres. Range times the angular size of a pixel,
     * divided by how edge-on the surface is — a glancing view spreads a pixel over far more
     * water, which is exactly where the aliasing is worst.
     */
    const toSurface = positionWorld.sub(cameraPosition);
    const range = toSurface.length();
    const grazing = max(normalize(toSurface).y.abs(), float(0.03));
    const footprint = range.mul(this.metresPerPixel).div(grazing);

    const detail = this.detailSlope(worldXZ, footprint);
    const slopeX = wave.slopeX.add(detail.x);
    const slopeZ = wave.slopeZ.add(detail.y);
    const worldNormal = normalize(vec3(slopeX.negate(), float(1), slopeZ.negate()));
    material.normalNode = transformNormalToView(worldNormal);

    // --- Fresnel ------------------------------------------------------------------------
    // Schlick's approximation with water's F0 of about 0.02. Grazing angles go to a mirror,
    // straight down goes to the body colour — the relationship that makes water read as water.
    const viewDirection = normalize(cameraPosition.sub(positionWorld));
    const cosTheta = clamp(dot(viewDirection, worldNormal), 0, 1);
    const fresnel = float(0.02).add(float(0.98).mul(pow(float(1).sub(cosTheta), float(5))));

    const body = mix(this.deep, this.shallow, cosTheta);
    // What a mirror at this angle would show is the sky, and the sky near the horizon is where
    // the water is most mirror-like — so the reflected colour is taken from the same horizon
    // radiance the aerial perspective converges on. It does not need a reflection pass to be
    // right, because at grazing angles almost all of what water reflects *is* the horizon sky.
    const reflected = mix(body.mul(vec3(this.skyRadiance)).mul(2.2), vec3(this.skyRadiance), clamp(fresnel, 0, 1));

    // --- sun glitter -----------------------------------------------------------------------
    /*
     * The glitter path.
     *
     * A single specular exponent gives a single hard highlight, which is what calm water does
     * and what open ocean never does. The width of the lobe is set by the *slope variance* of
     * the surface — how much the little facets are tilted — and Cox and Munk measured that
     * against wind speed from aerial photographs of sun glitter in 1954, which is as close to a
     * purpose-built reference as this is going to get: σ² ≈ 0.003 + 0.00512·U.
     *
     * Converting a slope variance to a Blinn–Phong exponent is `n ≈ 2/σ² − 2`, so a dead calm
     * (σ² = 0.003) gives ~660 and a 15 m/s gale gives ~25 — a pinpoint versus a broad shimmering
     * road to the horizon, from one number that the weather already owns.
     */
    const halfway = normalize(viewDirection.add(vec3(this.sunDirection)));
    const specularExponent = float(2).div(this.slopeVariance).sub(2).max(8);
    const glint = pow(clamp(dot(worldNormal, halfway), 0, 1), specularExponent);
    // Energy normalisation: a broader lobe spread over more solid angle must be dimmer per
    // steradian, or a windy sea reads as being lit by a brighter sun than a calm one.
    const glintEnergy = specularExponent.add(2).div(8);

    // --- whitecaps ---------------------------------------------------------------------------
    /*
     * Foam where the water is breaking.
     *
     * Driven by the Gerstner steepness sum rather than sprinkled by noise, so whitecaps appear
     * on the crests that are actually steep — which means they move with the waves, appear on
     * the windward faces, and get denser as the sea builds, all without being told to.
     */
    const foamMask = smoothstep(float(0.62), float(0.95), wave.steepness).mul(this.whitecapAmount);
    // A little high-frequency break-up so the foam has texture rather than being a smooth blob.
    const foamTexture = this.detailSlope(worldXZ.mul(3.1), footprint.mul(3.1))
      .x.abs()
      .mul(1.4)
      .add(0.55)
      .clamp(0, 1);
    const foam = foamMask.mul(foamTexture);

    // Cloud shadow, on the sun's contribution only — the reflected sky is skylight and a cloud
    // overhead does not dim it. See `clouds/cloudShadow.ts`.
    const sunOnWater = options.cloudShadow?.factorAt(positionWorld) ?? float(1);
    const withGlitter = reflected.add(
      vec3(this.sunRadiance).mul(glint.mul(glintEnergy).mul(1.6)).mul(sunOnWater),
    );
    material.colorNode = mix(withGlitter, vec3(0.86, 0.9, 0.94), foam);
    // Foam is rough; open water is not. Making this vary is what stops whitecaps reading as
    // white paint on a mirror.
    material.roughnessNode = mix(float(0.06), float(0.75), foam);

    this.material = material;
    this.mesh = new Mesh(geometry, material);
    this.mesh.name = 'ocean';
    this.mesh.frustumCulled = false;
    this.mesh.receiveShadow = false;
    this.mesh.castShadow = false;
    // Drawn after the terrain so the depth test does the coastline for us.
    this.mesh.renderOrder = 1;
    this.mesh.matrixAutoUpdate = false;

    this.applyQuality(options.settings);
  }

  /**
   * Sum the Gerstner waves, returning displacement, height, slope and steepness.
   *
   * Trochoidal rather than sinusoidal: each wave displaces *horizontally* toward its own crest
   * as well as vertically, which piles water up into a sharp crest and leaves a broad flat
   * trough. That asymmetry is the single most recognisable thing about the shape of ocean
   * waves, and a stack of sines does not have it at any amplitude.
   */
  private buildGerstner(worldXZ: Node<'vec2'>, spacing: Node<'float'>) {
    // Explicitly typed: these are rebound to the result of `.add()` on each iteration, and
    // without the annotation TypeScript pins them to the narrower type `float(0)` returns.
    let offsetX: Node<'float'> = float(0);
    let offsetZ: Node<'float'> = float(0);
    let height: Node<'float'> = float(0);
    let slopeX: Node<'float'> = float(0);
    let slopeZ: Node<'float'> = float(0);
    let steepness: Node<'float'> = float(0);

    const wind = vec2(this.windDirection);

    for (let i = 0; i < WAVES.length; i++) {
      const spec = WAVES[i];
      const k = (2 * Math.PI) / spec.wavelengthM; // wavenumber [1/m]
      // Deep-water dispersion: long waves travel faster, which is why a swell sorts itself out
      // into trains by wavelength as it crosses an ocean.
      const omega = Math.sqrt(GRAVITY * k);

      // Rotate the wind vector by this wave's offset to spread the directions.
      const angle = (spec.angleOffsetDeg * Math.PI) / 180;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      const direction = vec2(
        wind.x.mul(cosA).sub(wind.y.mul(sinA)),
        wind.x.mul(sinA).add(wind.y.mul(cosA)),
      );

      /*
       * The anti-alias fade. A wave needs at least a couple of vertices per wavelength to exist
       * at all; below that it is sampled into a lie. Fading it out over the band from two to
       * five samples per wavelength hands the detail over to the normal map smoothly rather
       * than switching it off at a visible ring.
       */
      const resolvable = smoothstep(
        float(spec.wavelengthM / 2),
        float(spec.wavelengthM / 5),
        spacing,
      );

      const amplitude = this.waveHeight
        .mul(spec.amplitudeScale * 0.5)
        .mul(this.waveEnable[i])
        .mul(resolvable)
        .mul(this.displacement);

      const phase = direction.dot(worldXZ).mul(k).sub(this.time.mul(omega));
      const cosPhase = cos(phase);
      const sinPhase = sin(phase);

      // Horizontal pull toward the crest — the trochoid.
      const q = spec.steepness / (k * WAVES.length);
      offsetX = offsetX.add(direction.x.mul(amplitude).mul(q * k).mul(cosPhase));
      offsetZ = offsetZ.add(direction.y.mul(amplitude).mul(q * k).mul(cosPhase));
      height = height.add(amplitude.mul(sinPhase));

      // Analytic slope — no finite differences, no extra samples.
      const slopeMagnitude = amplitude.mul(k).mul(cosPhase);
      slopeX = slopeX.add(direction.x.mul(slopeMagnitude));
      slopeZ = slopeZ.add(direction.y.mul(slopeMagnitude));

      // How close this wave is to breaking, accumulated across the set.
      steepness = steepness.add(amplitude.mul(k).mul(sinPhase.mul(-1).add(1)).mul(0.5));
    }

    return { offsetX, offsetZ, height, slopeX, slopeZ, steepness };
  }

  /**
   * Sub-vertex ripple, as slope rather than height.
   *
   * Arithmetic rather than a texture read, for the same reason the terrain's detail layer is:
   * the ocean is a huge fill-rate consumer and an integrated GPU has ALU going spare while its
   * texture units are the bottleneck. Four sines at incommensurate frequencies, advected by the
   * wind so the ripple travels rather than sitting still on a moving surface.
   */
  private detailSlope(worldXZ: Node<'vec2'>, footprintM: Node<'float'>): Node<'vec2'> {
    const drift = vec2(this.windDirection).mul(this.time).mul(0.8);
    const p = worldXZ.add(drift);

    /*
     * Each octave fades out once a pixel covers more than about a third of its wavelength.
     *
     * This is not optional polish — it is the difference between an ocean and a screen full of
     * moiré. These ripples are evaluated *analytically*, so unlike a texture they have no
     * mip chain and nothing band-limits them: a 20 m ripple viewed from two kilometres is
     * several cycles per pixel, and what the eye sees is not water but the interference
     * pattern between the ripple and the pixel grid. Fading each octave against the projected
     * pixel footprint is the analytic equivalent of picking a mip level.
     */
    const octave = (frequency: number, phase: number) => {
      const wavelength = (2 * Math.PI) / frequency;
      const fade = smoothstep(float(wavelength * 0.5), float(wavelength * 0.16), footprintM);
      return { value: sin(p.x.mul(frequency).add(p.y.mul(frequency * 0.6)).add(phase)), fade };
    };

    const a = octave(0.31, 0);
    const b = octave(-0.19, 1.7);
    const c = octave(0.71, 3.1);
    const d = octave(1.13, 0.4);

    const scale = this.waveHeight.mul(0.05).add(0.008);
    return vec2(
      a.value
        .mul(a.fade)
        .mul(0.5)
        .add(c.value.mul(c.fade).mul(0.3))
        .add(d.value.mul(d.fade).mul(0.2))
        .mul(scale),
      b.value
        .mul(b.fade)
        .mul(0.5)
        .add(c.value.mul(c.fade).mul(0.25))
        .add(d.value.mul(d.fade).mul(0.25))
        .mul(scale),
    );
  }

  setSunDirection(direction: Vector3): void {
    this.sunDirection.value.copy(direction).normalize();
  }

  /** Sunlight reaching the surface, so the glitter is the colour of the sun that made it. */
  setSunRadiance(r: number, g: number, b: number): void {
    this.sunRadiance.value.set(r, g, b);
  }

  /** The horizon sky's radiance, which is what a grazing reflection off water shows. */
  setSkyRadiance(r: number, g: number, b: number): void {
    this.skyRadiance.value.set(r, g, b);
  }

  /**
   * Tell the water how big a pixel is, so the detail normals can band-limit themselves.
   * Must be called on resize and on any field-of-view change.
   */
  setViewport(verticalFovRad: number, viewportHeightPx: number): void {
    this.metresPerPixel.value =
      (2 * Math.tan(verticalFovRad / 2)) / Math.max(1, viewportHeightPx);
  }

  /**
   * Set the sea state from the wind.
   *
   * @param speedMps     surface wind speed.
   * @param directionDeg the direction the wind blows *from*, degrees true (aviation convention).
   */
  setWind(speedMps: number, directionDeg: number): void {
    const speed = Math.max(0, speedMps);

    // Fully developed sea: significant wave height H ≈ 0.021·U². 5 m/s → 0.5 m, 15 m/s → 4.7 m.
    this.waveHeight.value = Math.min(9, 0.021 * speed * speed + 0.15);

    // Aviation gives the direction the wind comes *from*; waves travel the other way.
    const towardRad = ((directionDeg + 180) * Math.PI) / 180;
    // Render frame: X east, Z south, so a heading of 0 (north) is −Z.
    this.windDirection.value.set(Math.sin(towardRad), -Math.cos(towardRad));

    // Cox & Munk 1954, from sun-glitter photography.
    this.slopeVariance.value = 0.003 + 0.005_12 * speed;

    // Whitecaps begin around Beaufort 4 and cover the sea by Beaufort 8.
    this.whitecapAmount.value = clamp01((speed - 6) / 12);
  }

  applyQuality(settings: QualitySettings): void {
    const waves = settings.water.mode === 'gerstner' ? settings.water.gerstnerWaves : 0;
    for (let i = 0; i < this.waveEnable.length; i++) {
      this.waveEnable[i].value = i < waves ? 1 : 0;
    }
    // `flat` keeps the normals and the glitter — the tier switches off the *displacement*, which
    // is the part that costs vertex work, not the part that costs pixels.
    this.displacement.value = settings.water.mode === 'gerstner' ? 1 : 0;
  }

  /**
   * Advance the waves and keep the disc under the camera.
   *
   * Unlike M2's grid, the polar disc is *not* snapped to a coarse grid: its whole point is that
   * the fine rings stay under the camera, and snapping to a 2 km grid would put them up to a
   * kilometre away. The wave field is a function of world position and the patch origin is fed
   * to the shader, so the surface stays anchored to the planet however the mesh slides under it.
   */
  updateFrame(cameraX: number, cameraZ: number, dtSeconds: number): void {
    this.time.value += dtSeconds;
    this.mesh.position.set(cameraX, 0, cameraZ);
    this.mesh.updateMatrix();
    this.patchOrigin.value.set(cameraX, cameraZ);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

/** Radius of the innermost ring [m]. */
const INNER_RADIUS_M = 4;

/**
 * Ring-to-ring radius ratio for a given disc.
 *
 * 220 rings from 4 m to 220 km gives about 1.0501 — five percent per ring, so vertex spacing is
 * five percent of range everywhere. The shader needs this same number to know how finely the
 * mesh is sampled where it is shading, which is why it is computed once here and pushed as a
 * uniform rather than written down twice.
 */
function ringGrowthFor(radiusM: number, rings: number): number {
  return Math.pow(radiusM / INNER_RADIUS_M, 1 / rings);
}

/**
 * A disc whose rings are spaced geometrically from the centre.
 *
 * The innermost "ring" is a single fan around the centre; every ring after it is a quad strip.
 * Radii follow `r₀ · g^i`, so vertex spacing is proportional to range — see the note at the top
 * of this file for why that is the whole point.
 */
function buildPolarDisc(radiusM: number, rings: number, sectors: number): BufferGeometry {
  const innerM = INNER_RADIUS_M;
  const growth = ringGrowthFor(radiusM, rings);

  const vertexCount = 1 + rings * sectors;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);

  // The centre vertex.
  normals[1] = 1;

  let v = 1;
  for (let ring = 0; ring < rings; ring++) {
    const radius = innerM * Math.pow(growth, ring);
    for (let sector = 0; sector < sectors; sector++) {
      const angle = (sector / sectors) * Math.PI * 2;
      positions[v * 3] = Math.cos(angle) * radius;
      // Flat in the rest pose: the curvature drop depends on where the disc currently is on
      // the planet, not on where a vertex sits in the mesh, so the vertex stage owns it.
      positions[v * 3 + 1] = 0;
      positions[v * 3 + 2] = Math.sin(angle) * radius;
      normals[v * 3 + 1] = 1;
      uvs[v * 2] = radius / radiusM;
      uvs[v * 2 + 1] = sector / sectors;
      v++;
    }
  }

  const triangleCount = sectors + (rings - 1) * sectors * 2;
  const indices = new Uint32Array(triangleCount * 3);
  let i = 0;

  // The centre fan.
  for (let sector = 0; sector < sectors; sector++) {
    indices[i++] = 0;
    indices[i++] = 1 + sector;
    indices[i++] = 1 + ((sector + 1) % sectors);
  }
  // Then a quad strip per ring gap.
  for (let ring = 0; ring < rings - 1; ring++) {
    const inner = 1 + ring * sectors;
    const outer = inner + sectors;
    for (let sector = 0; sector < sectors; sector++) {
      const next = (sector + 1) % sectors;
      indices[i++] = inner + sector;
      indices[i++] = outer + sector;
      indices[i++] = outer + next;
      indices[i++] = inner + sector;
      indices[i++] = outer + next;
      indices[i++] = inner + next;
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(new BufferAttribute(indices, 1));
  // The disc follows the camera, so a bounding sphere fitted to its rest position would cull
  // it the moment the aircraft moved.
  geometry.boundingSphere = null;
  return geometry;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Exposed for the geometry test. */
export { INNER_RADIUS_M, ringGrowthFor, buildPolarDisc };
